import React, { useEffect, useRef, useState } from "react";
import { Send, X, Headset, Home, PhoneOff, FileText, Download } from "lucide-react";
import { publicApi, API_BASE } from "@/lib/api";
import { renderMarkdown } from "@/lib/format";
import { PRIYA_AVATAR } from "@/lib/brand";

// TRAI: valid Indian mobile = 10 digits starting 6-9 (after dropping a +91 prefix).
const isValidMobile = (v) => {
    let d = String(v || "").replace(/\D/g, "");
    if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
    else if (d.length === 13 && d.startsWith("091")) d = d.slice(3);
    return /^[6-9]\d{9}$/.test(d);
};

function BotBubble({ msg, onButton, onCapture, disabled, avatar, agentName }) {
    const [form, setForm] = useState({});
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e?.preventDefault();
        if (msg.capture) {
            const errs = {};
            for (const f of msg.capture) {
                if (f.required && !form[f.key]) {
                    errs[f.key] = `${f.label} is required`;
                    continue;
                }
                if (f.type === "tel" && form[f.key] && !isValidMobile(form[f.key])) {
                    errs[f.key] = "Please enter a valid 10-digit mobile number";
                }
            }
            if (Object.keys(errs).length) {
                setErrors(errs);
                return;
            }
        }
        setErrors({});
        setSubmitting(true);
        await onCapture(form);
        setSubmitting(false);
    };

    return (
        <div className="flex justify-start gap-2 animate-pop-in">
            <img
                src={avatar}
                alt={agentName}
                className="w-8 h-8 rounded-full object-cover ring-1 ring-black/10 shrink-0 mt-0.5"
            />
            <div className="max-w-[85%]">
                <div className="bg-white border border-stone-200 text-stone-800 rounded-2xl rounded-bl-sm p-3.5 shadow-card text-[14px] leading-relaxed">
                    <div
                        className="bot-text"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.text) }}
                    />
                </div>

                {msg.capture && (
                    <form
                        onSubmit={submit}
                        noValidate
                        className="mt-2 bg-white border border-stone-200 rounded-xl p-3.5 space-y-2.5 shadow-card"
                        data-testid="capture-form"
                    >
                        {msg.capture.map((f) => (
                            <div key={f.key}>
                                <label className="text-[11px] uppercase tracking-wider text-stone-500 block mb-1">
                                    {f.label}
                                    {f.required && <span className="text-orchid-600 ml-0.5">*</span>}
                                </label>
                                {f.type === "textarea" ? (
                                    <textarea
                                        rows={3}
                                        required={f.required}
                                        value={form[f.key] || ""}
                                        onChange={(e) =>
                                            setForm({ ...form, [f.key]: e.target.value })
                                        }
                                        placeholder={f.placeholder}
                                        data-testid={`capture-${f.key}`}
                                        className="w-full px-2.5 py-1.5 text-sm rounded-md border border-stone-200 focus:outline-none focus:ring-2 focus:ring-orchid-700/15 focus:border-orchid-700"
                                    />
                                ) : f.type === "select" ? (
                                    <select
                                        required={f.required}
                                        value={form[f.key] || ""}
                                        onChange={(e) =>
                                            setForm({ ...form, [f.key]: e.target.value })
                                        }
                                        data-testid={`capture-${f.key}`}
                                        className="w-full px-2.5 py-1.5 text-sm rounded-md border border-stone-200 bg-white focus:outline-none focus:ring-2 focus:ring-orchid-700/15 focus:border-orchid-700"
                                    >
                                        <option value="">Select…</option>
                                        {(f.options || []).map((o) => (
                                            <option key={o} value={o}>
                                                {o}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        type={f.type}
                                        required={f.required}
                                        value={form[f.key] || ""}
                                        inputMode={f.type === "tel" ? "numeric" : undefined}
                                        onChange={(e) => {
                                            let v = e.target.value;
                                            if (f.type === "tel") {
                                                v = v.replace(/\D/g, "");
                                                if (v.length > 10 && v.startsWith("91")) v = v.slice(2);
                                                v = v.slice(0, 10);
                                            }
                                            setForm({ ...form, [f.key]: v });
                                            if (errors[f.key]) setErrors({ ...errors, [f.key]: undefined });
                                        }}
                                        placeholder={f.type === "tel" ? "10-digit mobile number" : f.placeholder}
                                        data-testid={`capture-${f.key}`}
                                        className={`w-full px-2.5 py-1.5 text-sm rounded-md border focus:outline-none focus:ring-2 ${
                                            errors[f.key]
                                                ? "border-red-400 focus:ring-red-300"
                                                : "border-stone-200 focus:ring-orchid-700/15 focus:border-orchid-700"
                                        }`}
                                    />
                                )}
                                {errors[f.key] && (
                                    <p
                                        className="text-[11px] text-red-500 mt-1"
                                        data-testid={`capture-error-${f.key}`}
                                    >
                                        {errors[f.key]}
                                    </p>
                                )}
                            </div>
                        ))}
                        <button
                            type="submit"
                            disabled={submitting || disabled}
                            data-testid="capture-submit"
                            className="w-full mt-1 bg-[var(--wac)] hover:opacity-90 text-white text-sm font-medium py-2 rounded-md transition-opacity disabled:opacity-60"
                        >
                            {submitting ? "Submitting…" : "Submit"}
                        </button>
                    </form>
                )}

                {msg.buttons && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                        {msg.buttons.map((b) => (
                            <button
                                key={b.value}
                                onClick={() => onButton(b.value)}
                                disabled={disabled}
                                data-testid={`btn-${b.value}`}
                                className="text-xs font-medium px-3 py-1.5 rounded-full border border-stone-200 bg-white text-[var(--wac)] hover:bg-[var(--wac)] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-[var(--wac)]"
                            >
                                {b.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function AttachmentView({ attachment, light }) {
    const url = API_BASE + attachment.url;
    const size = attachment.size
        ? attachment.size > 1024 * 1024
            ? `${(attachment.size / 1048576).toFixed(1)} MB`
            : `${Math.max(1, Math.round(attachment.size / 1024))} KB`
        : "";
    if (attachment.is_image) {
        return (
            <a href={url} target="_blank" rel="noreferrer" data-testid="attachment-image-link">
                <img
                    src={url}
                    alt={attachment.filename}
                    data-testid="attachment-image"
                    className="rounded-xl max-h-52 max-w-full object-cover border border-black/10"
                />
            </a>
        );
    }
    return (
        <a
            href={url}
            target="_blank"
            rel="noreferrer"
            data-testid="attachment-file"
            className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 border ${
                light ? "bg-white/15 border-white/25" : "bg-white border-stone-200"
            }`}
        >
            <FileText size={22} className={light ? "text-white" : "text-[var(--wac)]"} />
            <span className="min-w-0 flex-1">
                <span className={`block text-[13px] font-medium truncate ${light ? "text-white" : "text-stone-800"}`}>
                    {attachment.filename}
                </span>
                <span className={`block text-[11px] ${light ? "text-white/70" : "text-stone-400"}`}>
                    {size ? `${size} · ` : ""}Tap to open
                </span>
            </span>
            <Download size={15} className={light ? "text-white/80" : "text-stone-400"} />
        </a>
    );
}

function UserBubble({ msg }) {
    if (msg.attachment) {
        return (
            <div className="flex justify-end animate-pop-in">
                <div className="max-w-[80%]">
                    <AttachmentView attachment={msg.attachment} light={false} />
                    {msg.text ? (
                        <div className="mt-1 bg-[var(--wac)] text-white rounded-2xl rounded-br-sm px-3.5 py-2.5 text-[14px] shadow-card">
                            {msg.text}
                        </div>
                    ) : null}
                </div>
            </div>
        );
    }
    return (
        <div className="flex justify-end animate-pop-in">
            <div className="max-w-[80%] bg-[var(--wac)] text-white rounded-2xl rounded-br-sm px-3.5 py-2.5 text-[14px] shadow-card">
                {msg.text}
            </div>
        </div>
    );
}

function AgentBubble({ msg, expertLabel }) {
    return (
        <div className="flex justify-start gap-2 animate-pop-in">
            <div className="w-8 h-8 rounded-full bg-sage-600 text-white shrink-0 mt-0.5 flex items-center justify-center">
                <Headset size={15} />
            </div>
            <div className="max-w-[85%]">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-sage-700 mb-1 ml-1">
                    {msg.agent_name || expertLabel || "Hospitality Expert"}
                </div>
                <div className="bg-sage-600/10 border border-sage-600/25 text-stone-800 rounded-2xl rounded-bl-sm p-3.5 text-[14px] leading-relaxed">
                    {msg.attachment ? (
                        <AttachmentView attachment={msg.attachment} light={false} />
                    ) : (
                        <div className="bot-text" dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.text) }} />
                    )}
                </div>
            </div>
        </div>
    );
}

function SystemNote({ text }) {
    return (
        <div className="flex justify-center animate-pop-in">
            <div className="max-w-[90%] text-center text-[11px] text-stone-500 bg-stone-100 rounded-full px-3.5 py-1.5">
                {text}
            </div>
        </div>
    );
}

function TypingIndicator() {
    return (
        <div className="flex justify-start animate-pop-in">
            <div className="bg-white border border-stone-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-card">
                <span className="typing-dot" />
                <span className="typing-dot" />
                <span className="typing-dot" />
            </div>
        </div>
    );
}

export default function Widget() {
    const [agentId] = useState(
        () => new URLSearchParams(window.location.search).get("agent") || "priya"
    );
    const [messages, setMessages] = useState([]);
    const [sessionId, setSessionId] = useState(null);
    const [input, setInput] = useState("");
    const [typing, setTyping] = useState(false);
    const [config, setConfig] = useState({ brand: { name: "Priya" } });
    const [handoff, setHandoff] = useState({ status: "none" });
    const [requesting, setRequesting] = useState(false);
    const [chatEnded, setChatEnded] = useState(false);
    const [awaitingLiveName, setAwaitingLiveName] = useState(false);
    const scrollRef = useRef(null);
    const pollAfterRef = useRef("");
    const guestNameRef = useRef("");

    const live = ["requested", "assigned", "active"].includes(handoff.status);
    const liveChatEnabled = config?.live_chat_enabled !== false;

    const accent = config?.brand?.accent || "#7E2A6C";
    const avatarUrl = config?.brand?.avatar_url || PRIYA_AVATAR;
    const agentName = config?.brand?.name || "Priya";
    const isHr = config?.brand?.kind === "hr";
    const expertLabel = isHr ? "HR Team" : "Hospitality Expert";

    useEffect(() => {
        publicApi
            .get("/api/widget/config", { params: { agent: agentId } })
            .then((r) => setConfig(r.data))
            .catch(() => {});
    }, [agentId]);

    const startSession = () => {
        const site = new URLSearchParams(window.location.search).get("site") || "";
        setChatEnded(false);
        setTyping(false);
        setHandoff({ status: "none" });
        pollAfterRef.current = "";
        return publicApi
            .post("/api/chat/start", { site, user_agent: navigator.userAgent, agent_id: agentId })
            .then((r) => {
                setSessionId(r.data.session_id);
                setMessages(r.data.bot_messages.map((m) => ({ ...m, role: "bot" })));
            })
            .catch((e) => {
                console.error(e);
                setMessages([
                    { role: "bot", text: "I'm having trouble connecting right now. Please refresh." },
                ]);
            });
    };

    useEffect(() => {
        startSession();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        scrollRef.current?.scrollTo({
            top: scrollRef.current.scrollHeight,
            behavior: "smooth",
        });
    }, [messages, typing]);

    // Poll for live-agent replies + timed waiting messages while a handoff is on.
    useEffect(() => {
        if (!sessionId || !live) return;
        let stopped = false;
        const tick = async () => {
            try {
                const { data } = await publicApi.get("/api/chat/poll", {
                    params: { session_id: sessionId, after: pollAfterRef.current || "" },
                });
                const incoming = data.messages || [];
                if (incoming.length) {
                    setMessages((prev) => [...prev, ...incoming]);
                    const lastTs = incoming[incoming.length - 1].timestamp;
                    if (lastTs) pollAfterRef.current = lastTs;
                }
                if (data.handoff) setHandoff(data.handoff);
            } catch (e) {
                /* transient poll error — retry next tick */
            }
        };
        tick();
        const id = setInterval(() => {
            if (!stopped) tick();
        }, 3000);
        return () => {
            stopped = true;
            clearInterval(id);
        };
    }, [sessionId, live]);

    const send = async (payload) => {
        if (!sessionId || typing) return;
        setTyping(true);
        try {
            const { data } = await publicApi.post("/api/chat/message", {
                session_id: sessionId,
                ...payload,
            });
            // During a live chat, agent/system messages arrive via polling — don't
            // double-render the (empty) bot response here.
            if (!data.live) {
                setMessages((prev) => [...prev, ...data.bot_messages.map((m) => ({ ...m, role: "bot" }))]);
            }
        } catch (e) {
            setMessages((prev) => [
                ...prev,
                { role: "bot", text: "Something went wrong. Please try again." },
            ]);
        } finally {
            setTyping(false);
        }
    };

    const requestAgent = async (guestName) => {
        if (!sessionId || requesting || live) return;
        setRequesting(true);
        try {
            const { data } = await publicApi.post("/api/chat/request-agent", {
                session_id: sessionId,
                ...(guestName ? { guest_name: guestName } : {}),
            });
            const added = (data.bot_messages || []).map((m) => ({ ...m, role: m.role || "bot" }));
            if (added.length) {
                setMessages((prev) => [...prev, ...added]);
                const lastTs = added[added.length - 1].timestamp;
                if (lastTs) pollAfterRef.current = lastTs;
            }
            setHandoff(data.handoff || { status: "requested" });
        } catch (e) {
            setMessages((prev) => [
                ...prev,
                { role: "bot", text: "I couldn't reach our team just now. Please try again shortly." },
            ]);
        } finally {
            setRequesting(false);
        }
    };

    // Ask the guest's name before connecting to a human (so the agent console
    // shows a real name, not a session id) — unless we already captured one.
    const startLiveAgent = () => {
        if (!sessionId || requesting || live) return;
        if (guestNameRef.current) {
            requestAgent(guestNameRef.current);
            return;
        }
        setAwaitingLiveName(true);
        setMessages((prev) => [
            ...prev,
            {
                role: "bot",
                text: "Before I connect you with our team, may I have your name?",
                capture: [{ key: "name", label: "Your name", type: "text", required: true }],
            },
        ]);
    };

    const endLiveIfNeeded = async () => {
        if (live && sessionId) {
            try {
                await publicApi.post("/api/chat/end", { session_id: sessionId });
            } catch (e) {
                /* ignore */
            }
            setHandoff({ status: "none" });
        }
    };

    const goHome = async () => {
        await endLiveIfNeeded();
        await startSession();
    };

    const endChat = async () => {
        await endLiveIfNeeded();
        setMessages((prev) => [
            ...prev,
            {
                role: "system",
                text: "Chat ended. Thank you 🌸  Tap Home to start a new chat anytime.",
            },
        ]);
        setChatEnded(true);
    };

    const onButton = (value) => {
        if (typing) return;
        // Special sentinel: a flow button that triggers the live-agent handoff.
        if (value === "__REQUEST_AGENT__") {
            const last = [...messages].reverse().find((m) => m.role === "bot" && m.buttons);
            const btn = last?.buttons?.find((b) => b.value === value);
            setMessages((prev) => [...prev, { role: "user", text: btn?.label || "Talk to a human" }]);
            startLiveAgent();
            return;
        }
        const last = [...messages].reverse().find((m) => m.role === "bot" && m.buttons);
        const btn = last?.buttons?.find((b) => b.value === value);
        setMessages((prev) => [...prev, { role: "user", text: btn?.label || value }]);
        send({ button_value: value });
    };

    const onCapture = async (captureData) => {
        if (typing) return;
        // Name asked right before a live-agent handoff → capture + connect.
        if (awaitingLiveName) {
            const nm = String(captureData?.name || "").trim();
            setAwaitingLiveName(false);
            guestNameRef.current = nm;
            setMessages((prev) => [...prev, { role: "user", text: nm }]);
            requestAgent(nm);
            return;
        }
        if (captureData?.name) guestNameRef.current = String(captureData.name).trim();
        const summary = Object.entries(captureData)
            .map(([k, v]) => (k === "otp" ? `OTP: ••••••` : `${k}: ${v}`))
            .join(" · ");
        setMessages((prev) => [...prev, { role: "user", text: summary }]);
        await send({ capture_data: captureData });
    };

    const onSendText = () => {
        if (typing) return;
        const v = input.trim();
        if (!v) return;
        setInput("");
        setMessages((prev) => [...prev, { role: "user", text: v }]);
        send({ text: v });
    };

    const closeWidget = () => {
        if (window.parent !== window) {
            window.parent.postMessage({ __orchidCc: "close" }, "*");
        }
    };

    // Only the most recent bot message is interactive — stale buttons/forms on
    // earlier messages are disabled so tapping always advances the flow.
    const lastBotIdx = messages.reduce((acc, m, i) => (m.role === "bot" ? i : acc), -1);

    return (
        <div className="h-screen w-full flex flex-col bg-ivory" style={{ "--wac": accent }}>
            {/* Header */}
            <header className="px-4 py-3 bg-white border-b border-stone-200 flex items-center gap-3 shadow-sm">
                <div className="relative shrink-0">
                    <img
                        src={avatarUrl}
                        alt={agentName}
                        data-testid="widget-avatar"
                        className="h-11 w-11 rounded-full object-cover ring-2 ring-black/10"
                    />
                    <span className="absolute bottom-0 right-0 w-3 h-3 bg-sage-600 border-2 border-white rounded-full" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-heading text-xl leading-none text-[var(--wac)]">
                        {agentName}
                    </div>
                    {live ? (
                        <div className="text-[10px] uppercase tracking-[0.15em] text-sage-700 mt-1 font-semibold" data-testid="handoff-status">
                            {handoff.status === "active"
                                ? `Live with ${handoff.agent_name || "our expert"}`
                                : "Connecting you to an expert…"}
                        </div>
                    ) : config?.brand?.tagline ? (
                        <div className="text-[10px] uppercase tracking-[0.2em] text-sage-600 mt-1">
                            {config.brand.tagline}
                        </div>
                    ) : null}
                </div>
                <button
                    data-testid="widget-close"
                    onClick={closeWidget}
                    className="p-1.5 hover:bg-stone-100 rounded transition-colors text-stone-500"
                    aria-label="Close"
                >
                    <X size={18} />
                </button>
            </header>

            {/* Action toolbar: Home · Live Agent · End Chat */}
            <div className="px-3 py-2 bg-white border-b border-stone-200 flex items-center gap-2">
                <button
                    onClick={goHome}
                    data-testid="widget-home"
                    title="Back to main menu"
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-stone-200 text-stone-600 hover:bg-stone-50 transition-colors"
                >
                    <Home size={14} /> Home
                </button>
                {!live && liveChatEnabled && (
                    <button
                        onClick={startLiveAgent}
                        disabled={requesting || !sessionId}
                        data-testid="chat-with-live-agent"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-sage-600 text-white hover:bg-sage-700 transition-colors disabled:opacity-60"
                    >
                        <Headset size={14} /> {requesting ? "Connecting…" : "Live Agent"}
                    </button>
                )}
                <div className="flex-1" />
                <button
                    onClick={endChat}
                    disabled={chatEnded}
                    data-testid="widget-end-chat"
                    title="End this chat"
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-red-200 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
                >
                    <PhoneOff size={13} /> End Chat
                </button>
            </div>

            {/* Messages */}
            <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto scroll-soft px-3 py-4 space-y-3"
                data-testid="widget-messages"
            >
                {messages.map((m, i) =>
                    m.role === "user" ? (
                        <UserBubble key={i} msg={m} />
                    ) : m.role === "agent" ? (
                        <AgentBubble key={i} msg={m} expertLabel={expertLabel} />
                    ) : m.role === "system" ? (
                        <SystemNote key={i} text={m.text} />
                    ) : (
                        <BotBubble
                            key={i}
                            msg={m}
                            onButton={onButton}
                            onCapture={onCapture}
                            avatar={avatarUrl}
                            agentName={agentName}
                            disabled={typing || chatEnded || i !== lastBotIdx}
                        />
                    )
                )}
                {typing && <TypingIndicator />}
            </div>

            {/* Input */}
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    onSendText();
                }}
                className="border-t border-stone-200 bg-white p-2.5 flex items-center gap-2"
                data-testid="widget-input-form"
            >
                <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    disabled={chatEnded}
                    placeholder={chatEnded ? "Chat ended — tap Home to start again" : live ? "Type your message to our expert…" : "Type your question…"}
                    data-testid="widget-input"
                    className="flex-1 px-3 py-2 text-sm rounded-full border border-stone-200 focus:outline-none focus:border-[var(--wac)] focus:ring-2 focus:ring-black/5 disabled:bg-stone-50 disabled:text-stone-400"
                />
                <button
                    type="submit"
                    data-testid="widget-send"
                    disabled={!input.trim() || chatEnded}
                    className="bg-[var(--wac)] hover:opacity-90 text-white p-2.5 rounded-full transition-opacity disabled:opacity-50 shrink-0"
                >
                    <Send size={16} />
                </button>
            </form>

            {/* Footer */}
            <div className="text-center text-[10px] uppercase tracking-[0.2em] text-stone-400 py-1.5 bg-white border-t border-stone-100">
                Powered by Orchid Concierge
            </div>
        </div>
    );
}
